import { useState, useEffect } from 'react';
import { User, Bell, Home, KeyRound, Loader2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../context/useAppContext';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/formatters';

export default function TenantSettings() {
    const { user } = useAppContext();
    const [name, setName] = useState(user.name);
    // email is read-only (managed by Supabase Auth)
    const [notifications, setNotifications] = useState({
        email_rent: true,
        email_maintenance: true,
        sms_alerts: false,
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Connected unit data fetched from Supabase
    const [tenantLink, setTenantLink] = useState<any>(null);
    const [property, setProperty] = useState<any>(null);
    const [unitLoading, setUnitLoading] = useState(true);

    useEffect(() => {
        const fetchUnit = async () => {
            if (!user.id && !user.email) { setUnitLoading(false); return; }
            setUnitLoading(true);
            try {
                let link: any = null;

                if (user.id) {
                    const { data } = await supabase
                        .from('tenant_links')
                        .select('*')
                        .eq('tenant_user_id', user.id)
                        .maybeSingle();
                    link = data;
                }

                if (!link && user.email) {
                    const { data } = await supabase
                        .from('tenant_links')
                        .select('*')
                        .eq('tenant_email', user.email)
                        .order('created_at', { ascending: false })
                        .limit(1)
                        .maybeSingle();
                    link = data;
                }

                setTenantLink(link);

                if (link?.property_id) {
                    const { data: prop } = await supabase
                        .from('properties')
                        .select('*')
                        .eq('id', link.property_id)
                        .single();
                    setProperty(prop);
                }
            } catch (err) {
                console.error('[SETTINGS] fetch unit error', err);
            } finally {
                setUnitLoading(false);
            }
        };
        fetchUnit();
    }, [user.id, user.email]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Update display name in user_metadata (best-effort for MVP)
            await supabase.auth.updateUser({ data: { name } });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error('[SETTINGS] save error', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page">
            <div className="page__header">
                <div>
                    <h1 className="page__title">Settings</h1>
                    <p className="page__subtitle">Manage your profile and preferences.</p>
                </div>
            </div>

            <div style={{ display: 'grid', gap: '1.75rem', maxWidth: 800 }}>

                {/* ── Connected Unit Panel ── */}
                <div className="data-panel">
                    <div className="data-panel__header" style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                        <h3 className="data-panel__title" style={{ display: 'flex', alignItems: 'center', margin: 0, gap: '8px' }}>
                            <Home size={18} color="var(--color-text-tertiary)" />
                            My Home
                        </h3>
                    </div>

                    <div style={{ padding: '1.5rem' }}>
                        {unitLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                <Loader2 size={18} className="animate-spin" />
                                Loading unit info…
                            </div>
                        ) : property ? (
                            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                                        <div style={{
                                            width: 48, height: 48, borderRadius: '12px',
                                            background: 'rgba(99, 102, 241, 0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Home size={22} color="var(--color-primary)" />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: '1.0625rem', color: 'var(--color-text)' }}>
                                                {property.name}
                                            </div>
                                            {property.address && (
                                                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginTop: '1px' }}>
                                                    {property.address}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                                        <SettingsRow label="Status">
                                            <span className={`badge badge--${tenantLink?.invite_status === 'active' ? 'green' : 'yellow'}`}>
                                                {tenantLink?.invite_status === 'active' ? 'Active' : 'Pending'}
                                            </span>
                                        </SettingsRow>
                                        {property.rent_amount && (
                                            <SettingsRow label="Monthly Rent">
                                                <strong>€{Number(property.rent_amount).toLocaleString()}</strong>
                                            </SettingsRow>
                                        )}
                                        {tenantLink?.lease_start && (
                                            <SettingsRow label="Lease Start">{formatDate(tenantLink.lease_start)}</SettingsRow>
                                        )}
                                        {tenantLink?.lease_end ? (
                                            <SettingsRow label="Lease End">{formatDate(tenantLink.lease_end)}</SettingsRow>
                                        ) : tenantLink?.lease_start ? (
                                            <SettingsRow label="Lease End">Ongoing</SettingsRow>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <p style={{ margin: '0 0 1rem', color: 'var(--color-text-secondary)', fontSize: '0.9375rem' }}>
                                        Your account isn't connected to a property yet. Enter your landlord's invite code to link your home.
                                    </p>
                                    <Link
                                        to="/app/tenant/connect"
                                        className="btn btn-primary btn-sm"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                    >
                                        <KeyRound size={15} />
                                        Enter Invite Code
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Personal Information ── */}
                <div className="data-panel">
                    <div className="data-panel__header" style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                        <h3 className="data-panel__title" style={{ display: 'flex', alignItems: 'center', margin: 0, gap: '8px' }}>
                            <User size={18} color="var(--color-text-tertiary)" />
                            Personal Information
                        </h3>
                    </div>
                    <form onSubmit={handleSave} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Display Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: '6px' }}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Email Address</label>
                                <input
                                    type="email"
                                    value={user.email}
                                    readOnly
                                    title="Email is managed by your auth provider"
                                    style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: '6px', background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)', cursor: 'not-allowed' }}
                                />
                                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', margin: 0 }}>
                                    Contact support to change your email.
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border-subtle)' }}>
                            {saved && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.875rem', color: '#16a34a' }}>
                                    <CheckCircle2 size={15} /> Saved!
                                </span>
                            )}
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* ── Notification Preferences ── */}
                <div className="data-panel">
                    <div className="data-panel__header" style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                        <h3 className="data-panel__title" style={{ display: 'flex', alignItems: 'center', margin: 0, gap: '8px' }}>
                            <Bell size={18} color="var(--color-text-tertiary)" />
                            Notification Preferences
                        </h3>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0' }}>
                        <NotifRow
                            label="Rent Reminders"
                            desc="Get notified 3 days before rent is due"
                            checked={notifications.email_rent}
                            onChange={(v) => setNotifications({ ...notifications, email_rent: v })}
                            first
                        />
                        <NotifRow
                            label="Maintenance Updates"
                            desc="Get status updates on your reported requests"
                            checked={notifications.email_maintenance}
                            onChange={(v) => setNotifications({ ...notifications, email_maintenance: v })}
                        />
                        <NotifRow
                            label="SMS Alerts"
                            desc="Important announcements via text message"
                            checked={notifications.sms_alerts}
                            onChange={(v) => setNotifications({ ...notifications, sms_alerts: v })}
                        />
                    </div>
                </div>

            </div>
        </div>
    );
}

/* ── Tiny helpers ─────────────────────────────────────────────────────── */

function SettingsRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', fontWeight: 500, minWidth: 96 }}>
                {label}
            </span>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text)', fontWeight: 500 }}>
                {children}
            </span>
        </div>
    );
}

function NotifRow({ label, desc, checked, onChange, first }: {
    label: string; desc: string; checked: boolean; onChange: (v: boolean) => void; first?: boolean;
}) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: first ? 0 : '1rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid var(--color-bg-secondary)',
        }}>
            <div>
                <div style={{ fontWeight: 500, fontSize: '0.9375rem' }}>{label}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '2px' }}>{desc}</div>
            </div>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer', flexShrink: 0 }}
            />
        </div>
    );
}
